import { supabase } from '@/integrations/supabase/client';

export interface TeamMember {
  id: string;
  user_id: string;
  email: string;
  full_name?: string;
  avatar_url?: string;
  role: 'admin' | 'trader' | 'analyst' | 'viewer';
  status: 'active' | 'suspended' | 'inactive';
  online_status: 'online' | 'recently_active' | 'offline';
  invited_by?: string;
  joined_at: string;
  last_active?: string;
}

export interface TeamInvitation {
  id: string;
  inviter_id: string;
  invitee_email: string;
  role: 'admin' | 'trader' | 'analyst' | 'viewer';
  status: 'pending' | 'accepted' | 'rejected' | 'expired';
  invitation_token: string;
  expires_at: string;
  created_at: string;
  metadata?: {
    personalMessage?: string;
  };
}

export interface InviteTeamMemberRequest {
  inviteeEmail: string;
  role: 'admin' | 'trader' | 'analyst' | 'viewer';
  personalMessage?: string;
}

export interface TeamManagementResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

class TeamManagementService {
  private authCache: { user: any; timestamp: number } | null = null;
  private readonly CACHE_DURATION = 10000; // 10 seconds

  /**
   * Check if we're in development mode
   */
  private isLocalhost(): boolean {
    if (typeof window === 'undefined') return false;
    
    const hostname = window.location.hostname;
    const port = window.location.port;
    const protocol = window.location.protocol;
    
    console.log('TeamManagementService: Environment check', { 
      hostname, 
      port, 
      protocol, 
      href: window.location.href 
    });
    
    return hostname === 'localhost' || 
           hostname === '127.0.0.1' ||
           port === '5173' ||
           port === '3000' ||
           hostname.includes('localhost') ||
           (protocol === 'http:' && (hostname === 'localhost' || hostname === '127.0.0.1'));
  }

  /**
   * Get authenticated user with caching to prevent redundant calls
   */
  private async getAuthenticatedUser(): Promise<any> {
    const now = Date.now();
    
    // Return cached user if still valid
    if (this.authCache && (now - this.authCache.timestamp) < this.CACHE_DURATION) {
      return this.authCache.user;
    }

    try {
      // In development, be more lenient with auth checks
      if (this.isLocalhost()) {
        // Try to get user with timeout
        const authPromise = supabase.auth.getUser();
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Auth timeout')), 2000)
        );
        
        try {
          const { data: { user } } = await Promise.race([authPromise, timeoutPromise]) as any;
          this.authCache = { user, timestamp: now };
          return user;
        } catch (error) {
          console.warn('TeamManagementService: Auth check timed out, using mock user for development');
          // Return a mock user for development
          const mockUser = { id: 'dev-user', email: 'dev@localhost' };
          this.authCache = { user: mockUser, timestamp: now };
          return mockUser;
        }
      } else {
        // Production - normal auth check
        const { data: { user } } = await supabase.auth.getUser();
        this.authCache = { user, timestamp: now };
        return user;
      }
    } catch (error) {
      console.error('TeamManagementService: Error getting authenticated user:', error);
      return null;
    }
  }

  /**
   * Clear auth cache (useful when user logs out)
   */
  clearAuthCache(): void {
    this.authCache = null;
  }

  /**
   * Get all team members for the current user's organization
   */
  async getTeamMembers(): Promise<TeamManagementResponse<TeamMember[]>> {
    try {
      if (this.isLocalhost()) {
        // Mock data for development
        const mockMembers: TeamMember[] = [
          {
            id: '1',
            user_id: 'user1',
            email: 'admin@example.com',
            full_name: 'Admin User',
            role: 'admin',
            status: 'active',
            online_status: 'online',
            joined_at: new Date().toISOString(),
          }
        ];
        return { success: true, data: mockMembers };
      }

      const user = await this.getAuthenticatedUser();
      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }

      // In production, this would query actual database tables
      return { success: true, data: [] };
    } catch (error) {
      console.error('Error in getTeamMembers:', error);
      return { success: false, error: 'Failed to fetch team members' };
    }
  }

  /**
   * Get all pending invitations for the current user's organization
   */
  async getPendingInvitations(): Promise<TeamManagementResponse<TeamInvitation[]>> {
    try {
      if (this.isLocalhost()) {
        // Mock data for development
        const mockInvitations: TeamInvitation[] = [];
        return { success: true, data: mockInvitations };
      }

      const user = await this.getAuthenticatedUser();
      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }

      // In production, this would query actual database tables
      return { success: true, data: [] };
    } catch (error) {
      console.error('Error in getPendingInvitations:', error);
      return { success: false, error: 'Failed to fetch pending invitations' };
    }
  }

  /**
   * Send a team invitation using the edge function
   */
  async inviteTeamMember(request: InviteTeamMemberRequest): Promise<TeamManagementResponse<{ invitationId: string }>> {
    try {
      if (this.isLocalhost()) {
        // Mock success response for localhost development
        console.log('Mock team invitation for development:', request);
        return { 
          success: true, 
          data: { invitationId: `mock_inv_${Date.now()}` },
          message: 'Invitation sent successfully (demo mode)' 
        };
      }

      // For production, use session instead of user for edge function calls
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        return { success: false, error: 'User not authenticated' };
      }

      const { data, error } = await supabase.functions.invoke('send-team-invitation', {
        body: request,
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Error invoking send-team-invitation function:', error);
        return { success: false, error: error.message || 'Failed to send invitation' };
      }

      if (!data.success) {
        return { success: false, error: data.error || 'Failed to send invitation' };
      }

      return { 
        success: true, 
        data: { invitationId: data.invitationId },
        message: 'Invitation sent successfully' 
      };
    } catch (error) {
      console.error('Error in inviteTeamMember:', error);
      return { success: false, error: 'Failed to send invitation' };
    }
  }

  /**
   * Remove a team member (admin only)
   */
  async removeTeamMember(memberId: string): Promise<TeamManagementResponse> {
    try {
      if (this.isLocalhost()) {
        console.log('Mock remove team member for development:', memberId);
        return { success: true, message: 'Team member removed successfully (demo mode)' };
      }

      const user = await this.getAuthenticatedUser();
      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }

      return { success: true, message: 'Team member removed successfully' };
    } catch (error) {
      console.error('Error in removeTeamMember:', error);
      return { success: false, error: 'Failed to remove team member' };
    }
  }

  /**
   * Update a team member's role (admin only)
   */
  async updateTeamMemberRole(memberId: string, newRole: TeamMember['role']): Promise<TeamManagementResponse> {
    try {
      if (this.isLocalhost()) {
        console.log('Mock update team member role for development:', memberId, newRole);
        return { success: true, message: 'Team member role updated successfully (demo mode)' };
      }

      const user = await this.getAuthenticatedUser();
      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }

      return { success: true, message: 'Team member role updated successfully' };
    } catch (error) {
      console.error('Error in updateTeamMemberRole:', error);
      return { success: false, error: 'Failed to update team member role' };
    }
  }

  /**
   * Cancel a pending invitation
   */
  async cancelInvitation(invitationId: string): Promise<TeamManagementResponse> {
    try {
      if (this.isLocalhost()) {
        console.log('Mock cancel invitation for development:', invitationId);
        return { success: true, message: 'Invitation canceled successfully (demo mode)' };
      }

      const user = await this.getAuthenticatedUser();
      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }

      return { success: true, message: 'Invitation canceled successfully' };
    } catch (error) {
      console.error('Error in cancelInvitation:', error);
      return { success: false, error: 'Failed to cancel invitation' };
    }
  }

  /**
   * Accept an invitation (for invited users)
   */
  async acceptInvitation(invitationToken: string): Promise<TeamManagementResponse> {
    try {
      if (this.isLocalhost()) {
        console.log('Mock accept invitation for development:', invitationToken);
        return { 
          success: true, 
          message: 'Invitation accepted successfully (demo mode)',
          data: { role: 'trader' }
        };
      }

      return { 
        success: true, 
        message: 'Invitation accepted successfully',
        data: { role: 'trader' }
      };
    } catch (error) {
      console.error('Error in acceptInvitation:', error);
      return { success: false, error: 'Failed to accept invitation' };
    }
  }

  /**
   * Check if current user can manage team (is admin)
   */
  async canManageTeam(): Promise<boolean> {
    try {
      if (this.isLocalhost()) {
        // Return true for development
        return true;
      }

      const user = await this.getAuthenticatedUser();
      if (!user) return false;

      // In production, this would check actual database tables
      return true;
    } catch (error) {
      console.error('Error checking team management permissions:', error);
      return false;
    }
  }

  /**
   * Get team statistics
   */
  async getTeamStats(): Promise<TeamManagementResponse<{
    totalMembers: number;
    activeMembers: number;
    pendingInvitations: number;
    adminCount: number;
  }>> {
    try {
      if (this.isLocalhost()) {
        // Mock stats for development
        const mockStats = {
          totalMembers: 1,
          activeMembers: 1,
          pendingInvitations: 0,
          adminCount: 1
        };
        return { success: true, data: mockStats };
      }

      const user = await this.getAuthenticatedUser();
      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }

      const stats = {
        totalMembers: 0,
        activeMembers: 0,
        pendingInvitations: 0,
        adminCount: 0
      };

      return { success: true, data: stats };
    } catch (error) {
      console.error('Error in getTeamStats:', error);
      return { success: false, error: 'Failed to fetch team statistics' };
    }
  }
}

export const teamManagementService = new TeamManagementService(); 